import {jsonToCSV} from 'react-papaparse';

import moment from 'moment/moment';
import {GrantStatus} from 'src/openapi/admin/models/GrantStatus';
import {mapDateFormat} from 'src/utils/mapDateFormat';

import type {ResourceView} from 'src/modules/entitlements/typedef';
import type {EntitlementTransferContext} from 'src/openapi/admin/models/EntitlementTransferContext';
import type {MigrationRelationResult} from 'src/openapi/admin/models/MigrationRelationResult';
import type {Relation} from 'src/openapi/admin/models/Relation';

export type RelationsIndex = {
    byRecordId: Map<string, Relation>,
    byRoleId: Map<string, Relation[]>,
};

const createRelationKey = (relation: Relation) => `${relation.resourceId}-${relation.grantId}-${relation.roleId || ''}`;

export const mergeRelationsIndex = (base: RelationsIndex, addition: RelationsIndex): RelationsIndex => {
    const mergedByRecordId = new Map(base.byRecordId);
    addition.byRecordId.forEach((relation, recordId) => {
        mergedByRecordId.set(recordId, relation);
    });

    const mergedByRoleId = new Map(base.byRoleId);
    addition.byRoleId.forEach((relations, roleId) => {
        const existingRelations = mergedByRoleId.get(roleId);

        if (!existingRelations) {
            mergedByRoleId.set(roleId, relations.slice());
            return;
        }

        const deduped = new Map(existingRelations.map(relation => [createRelationKey(relation), relation]));
        relations.forEach(relation => {
            deduped.set(createRelationKey(relation), relation);
        });
        mergedByRoleId.set(roleId, Array.from(deduped.values()));
    });

    return {
        byRecordId: mergedByRecordId,
        byRoleId: mergedByRoleId,
    };
};

export const buildRelationsIndex = (rows: ResourceView[]): RelationsIndex => {
    const byRecordId = new Map<string, Relation>();
    const byRoleId = new Map<string, Relation[]>();

    rows.forEach(({recordId}) => {
        let parsedRecord: {resourceId?: string, grantId?: string, roleId?: string} | null = null;

        try {
            parsedRecord = JSON.parse(recordId);
        } catch {
            return;
        }

        if (!parsedRecord) {
            return;
        }

        const {resourceId, grantId, roleId} = parsedRecord;

        if (!resourceId || !grantId) {
            return;
        }

        const relation: Relation = roleId
            ? {resourceId, grantId, roleId}
            : {resourceId, grantId};

        byRecordId.set(recordId, relation);

        if (roleId) {
            const existingRelations = byRoleId.get(roleId) || [];
            existingRelations.push(relation);
            byRoleId.set(roleId, existingRelations);
        }
    });

    return {byRecordId, byRoleId};
};

export const buildRelationsFromSelection = (
    selected: Array<string | number>,
    relationsIndex: RelationsIndex,
): Relation[] => {
    const relations: Relation[] = [];
    const seenKeys = new Set<string>();

    selected.forEach(selectedId => {
        const id = String(selectedId);
        let parsedId: {resourceId?: string, grantId?: string, roleId?: string} | null = null;

        try {
            parsedId = JSON.parse(id);
        } catch {
            return;
        }

        if (!parsedId) {
            return;
        }

        if (parsedId.roleId && !parsedId.resourceId) {
            const roleRelations = relationsIndex.byRoleId.get(parsedId.roleId) || [];
            roleRelations.forEach(relation => {
                const key = createRelationKey(relation);
                if (!seenKeys.has(key)) {
                    seenKeys.add(key);
                    relations.push(relation);
                }
            });
            return;
        }

        const relation = relationsIndex.byRecordId.get(id);
        if (relation) {
            const key = createRelationKey(relation);
            if (!seenKeys.has(key)) {
                seenKeys.add(key);
                relations.push(relation);
            }
        }
    });
    return relations;
};

const buildTimestamp = (dateFormat: string) => {
    const defaultFormat = mapDateFormat('YYYYMMDD', 'FILE_NAME', 'DATE_TIME');
    const mappedFormat = mapDateFormat(dateFormat, 'FILE_NAME', 'DATE_TIME');
    const safeFormat = mappedFormat === 'invalid format' ? defaultFormat : mappedFormat;

    return moment().format(safeFormat);
};

const withTimestamp = (filename: string, timestamp: string) => {
    if (!timestamp) {
        return filename;
    }

    const lastDotIndex = filename.lastIndexOf('.');

    if (lastDotIndex === -1) {
        return `${filename}_${timestamp}`;
    }

    const name = filename.slice(0, lastDotIndex);
    const extension = filename.slice(lastDotIndex);

    return `${name}_${timestamp}${extension}`;
};

export const downloadEntitlementTransferContext = (
    context: EntitlementTransferContext,
    filename = 'entitlements-export.json',
    dateFormat = 'YYYYMMDD',
) => {
    const blob = new Blob([JSON.stringify(context, null, 2)], {type: 'application/json'});
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const timestamp = buildTimestamp(dateFormat);
    link.download = withTimestamp(filename, timestamp);
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
};

type ImportResultReportRow = {
    '#': string,
    'Role': string,
    'Resources': string,
    'Grant Name': string,
    'Grant Status': string,
    'Action Status': string,
};

const formatActionStatus = (errors?: string[]) => {
    if (!errors || errors.length === 0) {
        return 'Completed';
    }

    return errors.join('; ');
};

export const downloadImportResultsReport = (
    results: MigrationRelationResult[],
    filename = 'entitlements-import-report.csv',
    dateFormat = 'YYYYMMDD',
) => {
    if (!results.length) {
        return;
    }

    const headers: ImportResultReportRow = {
        '#': '#',
        'Role': 'Role',
        'Resources': 'Resources',
        'Grant Name': 'Grant Name',
        'Grant Status': 'Grant Status',
        'Action Status': 'Action Status',
    };

    const rows: ImportResultReportRow[] = results.map((relation, index) => ({
        '#': String(index + 1),
        'Role': relation.roleName || '',
        'Resources': relation.resourceName,
        'Grant Name': relation.grantName,
        'Grant Status': relation.grantStatus,
        'Action Status': formatActionStatus(relation.errors),
    }));

    const csv = jsonToCSV([headers, ...rows], {
        delimiter: ',',
        header: false,
        newline: '\r\n',
        quotes: true,
        skipEmptyLines: true,
    });

    const blob = new Blob([csv], {type: 'text/csv'});
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const timestamp = buildTimestamp(dateFormat);
    link.download = withTimestamp(filename, timestamp);
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
};

export const ENTITLEMENTS_IMPORT_MAX_RECORDS = 250;
const JSON_MIME_TYPES = ['application/json', 'text/json'];

type ParsedContext = Partial<EntitlementTransferContext>;

const validateRelationKeys = (relation: Record<string, unknown>, rowIndex: number) => {
    const allowedKeys = [
        'resourceExternalId',
        'resourceName',
        'grantExternalId',
        'grantName',
        'grantStatus',
        'roleExternalId',
        'roleName',
    ];
    const requiredKeys = allowedKeys.slice(0, 5);

    const relationKeys = Object.keys(relation);
    const missingKeys = requiredKeys.filter(key => !relationKeys.includes(key));
    if (missingKeys.length) {
        throw new Error(`Missing field(s) ${missingKeys.join(', ')} in entitlements entry #${rowIndex + 1}`);
    }

    const unexpectedKeys = relationKeys.filter(key => !allowedKeys.includes(key));
    if (unexpectedKeys.length) {
        throw new Error(`Unexpected field(s) ${unexpectedKeys.join(', ')} in entitlements entry #${rowIndex + 1}`);
    }

    requiredKeys.forEach(key => {
        if (typeof relation[key] !== 'string' || !relation[key]) {
            throw new Error(`Field "${key}" must be a non-empty string in entitlements entry #${rowIndex + 1}`);
        }
    });

    if (!Object.values(GrantStatus).includes(relation.grantStatus as GrantStatus)) {
        throw new Error(`Invalid grantStatus value in entitlements entry #${rowIndex + 1}`);
    }

    if ('roleExternalId' in relation && relation.roleExternalId && typeof relation.roleExternalId !== 'string') {
        throw new Error(`Field "roleExternalId" must be a string in entitlements entry #${rowIndex + 1}`);
    }

    if ('roleName' in relation && relation.roleName && typeof relation.roleName !== 'string') {
        throw new Error(`Field "roleName" must be a string in entitlements entry #${rowIndex + 1}`);
    }
};

export const parseEntitlementTransferContextFile = async (file: File): Promise<EntitlementTransferContext> => {
    if (!file) {
        throw new Error('No file provided');
    }

    const fileName = file.name.toLowerCase();
    const isJsonType = !file.type || JSON_MIME_TYPES.includes(file.type);
    const hasJsonExtension = fileName.endsWith('.json');

    if (!isJsonType && !hasJsonExtension) {
        throw new Error('Invalid file format. Accepted format: JSON');
    }

    let payload: ParsedContext | null = null;
    try {
        const content = await file.text();
        payload = JSON.parse(content) as ParsedContext;
    } catch {
        throw new Error('File contains invalid JSON');
    }

    if (!payload || typeof payload !== 'object') {
        throw new Error('Invalid file structure');
    }

    const {bindings, objects} = payload;

    if (!objects || typeof objects !== 'object') {
        throw new Error('Missing objects section');
    }

    const expectedObjectArrays: Array<keyof EntitlementTransferContext['objects']> = [
        'namespaces',
        'resources',
        'grants',
        'roles',
    ];

    expectedObjectArrays.forEach(key => {
        if (!Array.isArray((objects as EntitlementTransferContext['objects'] | undefined)?.[key])) {
            throw new TypeError(`Invalid or missing "${key}" collection inside objects section`);
        }
    });

    if (!Array.isArray(bindings)) {
        throw new TypeError('Missing entitlements list');
    }

    if (!bindings.length) {
        throw new Error('File does not contain entitlements');
    }

    if (bindings.length > ENTITLEMENTS_IMPORT_MAX_RECORDS) {
        throw new Error(`File contains more than ${ENTITLEMENTS_IMPORT_MAX_RECORDS} entitlements`);
    }

    bindings.forEach((relation, index) => {
        if (!relation || typeof relation !== 'object') {
            throw new Error(`Invalid entitlements entry at row #${index + 1}`);
        }
        validateRelationKeys(relation as Record<string, unknown>, index);
    });

    return payload as EntitlementTransferContext;
};
